/* eslint-disable react-hooks/exhaustive-deps */
import { BaseTable, Card, FooterMenu } from "components"
import moment from "moment"
import "moment/locale/id"
import { Fragment, useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useApi, useHandleAuth, useToggle } from "utils"
import { today } from "utils/functions"
import {
  BalanceSheetComparisonType,
  BalanceSheetNoneType,
  BalanceSheetReportType,
  DateType,
  FilterReport,
  ResponseBS
} from "../../types"
import { periode } from "../../utils/vars"
import { FilterBS, Item } from "./components"
import { ItemTotal } from "./components/ItemTotal"
import {
  calculateParent,
  generateDateHeaderColumn,
  generateNetAsset,
  handleExportExcelBalanceSheet
} from "./utils/functions"
import { useHandleExportPdf } from "./utils/hooks/useHandleExportPdf"
import { useCollapseState } from "../../utils"
import { DataStatusProfitNLoss, HeaderProfitNLoss } from "../TabProfitLoss/components"

export const TabBalanceSheet = () => {
  const api = useApi()
  const auth = useHandleAuth()
  const token = auth.userTokenData()
  const toggleAccountNumber = useToggle(true)
  const toggleZeroValue = useToggle(true)
  const toggleSubHeader = useToggle(true)
  const toggleDetail = useToggle(true)
  const [comparisonDate, setComparisonDate] = useState<string[]>([])
  const [reportData, setReportData] = useState<BalanceSheetReportType[]>([])
  const nonZeroReportData: BalanceSheetReportType[] = reportData.filter(
    (item) => item.balance.some((balance) => balance !== 0)
  )
  const data = toggleZeroValue.isActive ? reportData : nonZeroReportData
  const exportPdf = useHandleExportPdf({
    data: data,
    comparisonDates: comparisonDate
  })

  // const finalBalance = data.reduce((acc, item) => {
  //   if (["LIABILITY", "EQUITY"].includes(item.coa_name)) {
  //     return acc + item.balance[0]
  //   }

  //   return acc
  // }, 0)

  const finalBalance = data
    .filter(item => ["LIABILITY", "EQUITY"].includes(item.coa_name))
    .reduce<number[]>((acc, item) => {
      if (acc.length === 0) acc = Array(item.balance.length).fill(0);
      return acc.map((val, idx) => val + (item.balance[idx] || 0));
    }, []);


  const dateTypeBalanceSheet = localStorage?.getItem("dateTypeBalanceSheet")
  const DateStartBalanceSheet = localStorage?.getItem("DateStartBalanceSheet")

  const methods = useForm<FilterReport>({
    defaultValues: {
      dateType: dateTypeBalanceSheet ? dateTypeBalanceSheet as DateType : "1",
      date: DateStartBalanceSheet ? DateStartBalanceSheet : today(),
      periode: periode[0],
      comparison: undefined,
      filter: "0"
    }
  })

  const [date, periodValue] = methods.watch(["date", "periode.value"])

  const onSubmitFilter = async (data: FilterReport) => {
    setComparisonDate([])

    const isFilterAll = Boolean(data.filter === "0")

    await api
      .postForm<{ payload: ResponseBS }>("/balancesheet/", {
        DateType: data.dateType,
        DateBalance: data.date,
        PeriodType: data.periode.value,
        Comparison: data.comparison
      })
      .then((res) => {
        /**
         * Should pay attention to the response type, the response has 2 different type.
         * if the comparation is exist, the response will be an array of array and need to be flatten for simplicity
         * else, if the comparation is undefined, the response will be an array of object
         **/

        if (data.comparison) {
          const payload = res.data.payload as BalanceSheetComparisonType[][]

          const flattenPayload = payload.flat()

          const dateCompare = flattenPayload.map(
            ({ DateCompare }) => DateCompare
          )

          const calculate = flattenPayload.map((data) => {
            return {
              ...data,
              BSData: calculateParent(data.BSData)
            }
          })

          const filterByCoaGrup = isFilterAll
            ? calculate.map((row) => ({
              DateCompare: row.DateCompare,
              BSData: generateNetAsset(row.BSData)
            }))
            : calculate.map((row) => ({
              DateCompare: row.DateCompare,
              BSData: row.BSData.filter(
                (coa) => coa.group_coa_code === data.filter
              )
            }))

          const mapBalanceToArray = filterByCoaGrup[0].BSData?.map((data) => ({
            ...data,
            balance: calculate.map(
              ({ BSData }) =>
                BSData?.find(({ coa_id }) => coa_id === data.coa_id)?.balance ||
                0
            )
          }))

          setComparisonDate(dateCompare)
          setReportData(mapBalanceToArray)
        } else {
          const date = moment(data.date)

          const payload = res.data.payload as BalanceSheetNoneType[]

          const dateCompare = [date.format("YYYY-MM-DD")]

          const calculate = calculateParent(payload)

          const filterByCoaGrup = isFilterAll
            ? generateNetAsset(calculate)
            : calculate.filter((coa: any) => coa.group_coa_code === data.filter)

          const mapBalanceToArray = filterByCoaGrup.map((data) => ({
            ...data,
            balance: [data.balance]
          }))

          setComparisonDate(dateCompare)
          setReportData(mapBalanceToArray)
        }
      })
      .catch((err) => console.error(err))
  }

  const handleExport = () =>
    handleExportExcelBalanceSheet({
      comparisonDates: comparisonDate,
      periodValue,
      data: data as BalanceSheetReportType[],
      date: methods.getValues().date,
      companyName: token?.company_name ?? ""
    })

  useEffect(() => {
    if (methods.formState.submitCount === 0) {
      methods.handleSubmit((data) => onSubmitFilter(data))()
    }
  }, [])

  const { isCollapsed, toggleCollapse } = useCollapseState(data, "coaBS")

  const isFetching = methods.formState.isSubmitting

  return (
    <Fragment>
      <section className="mt-2">
        <Card>
          <Card.Body>
            <FormProvider {...methods}>
              <FilterBS
                toggleAccountNumber={toggleAccountNumber}
                toggleZeroValue={toggleZeroValue}
                toggleSubHeader={toggleSubHeader}
                toggleDetail={toggleDetail}
                onSubmit={(data) => {
                  onSubmitFilter(data)
                  localStorage.setItem("dateTypeBalanceSheet", data?.dateType as DateType)
                  localStorage.setItem("DateStartBalanceSheet", data?.date as DateType)
                }}
              />
            </FormProvider>
          </Card.Body>
        </Card>
      </section>


      <section className="mt-2">
        <Card className="mb-2">
          <HeaderProfitNLoss label="Balance Sheet" singleDate startDate={date} />
          <Card.Body className="overflow-x-auto max-h-[calc(100vh-640px)]">
            <div className="overflow-x-auto">
              <BaseTable className="border-spacing-0">
                <thead>
                  <tr className="uppercase">
                    <th></th>
                    {comparisonDate?.map((date) => (
                      <th key={date} className="text-right">
                        {generateDateHeaderColumn(periodValue, date)}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {!reportData?.length || isFetching ? <DataStatusProfitNLoss loading={isFetching} /> : (
                    <>
                      {data.map((item, key) => {
                        if (item.parent_id === -1) {
                          return (
                            <>
                              <Item
                                key={item.coa_id + key}
                                list={data}
                                item={item}
                                displayAccountNumber={toggleAccountNumber.isActive}
                                displaySubHeader={toggleSubHeader.isActive}
                                displayDetail={toggleDetail.isActive}
                                isCollapsed={isCollapsed}
                                toggleCollapse={toggleCollapse}
                                dateTo={date}
                                comparisonDates={comparisonDate}
                              />

                              {item.coa_name === "NET ASSETS" ? null : <ItemTotal item={item} />}
                            </>
                          )
                        }

                        return <></>
                      })}

                      <ItemTotal
                        item={{
                          balance: finalBalance,
                          coa_id: 0,
                          coa_level: 1,
                          coa_name: "LIABILITIES & SHAREHOLDER'S AQUITY",
                          coacode: "",
                          group_coa_code: "",
                          group_coa_id: "",
                          header_level: 1,
                          linked_code: "",
                          normal_pos: 0,
                          parent_id: 0
                        }}
                      />
                    </>
                  )}
                </tbody>
              </BaseTable>
            </div>
          </Card.Body>
        </Card>

        <FooterMenu
          export={{
            actiontype: "export",
            onClick: handleExport
          }}
          pdf={{
            actiontype: "pdf",
            onClick: () =>
              exportPdf.handleExportPdf({
                date: methods.getValues().date,
                displayAccountNumber: false
              })
          }}
        />
      </section>
    </Fragment>
  )
}
